import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFileExcel} from '@fortawesome/free-solid-svg-icons';

/**
 * Export type selection components
 */
export const ExportTypeSelector = ({exportType, setExportType}) => {
  return (
    <div className="space-y-2 mb-3">
      {/* Raw Measurement Data Option */}
      <button
        type="button"
        onClick={() => setExportType('raw_measurement')}
        className={`w-full text-left p-3 rounded-lg border transition-all ${
          exportType === 'raw_measurement'
            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 ring-2 ring-teal-500 ring-opacity-50'
            : 'border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 hover:border-teal-300 dark:hover:border-teal-700'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13h2l2 5 4-12 3 7h2l2-3h2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">Raw Measurement Data</span>
              <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">CSV</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Raw measurement readings for selected period and resource</p>
          </div>
          {exportType === 'raw_measurement' && (
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      </button>

      {/* Historical Forecast Option */}
      <button
        type="button"
        onClick={() => setExportType('historical_forecast')}
        className={`w-full text-left p-3 rounded-lg border transition-all ${
          exportType === 'historical_forecast'
            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 ring-2 ring-brand-500 ring-opacity-50'
            : 'border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 hover:border-brand-300 dark:hover:border-brand-700'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">Historical Forecast Data</span>
              <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">CSV</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Your raw forecast submissions for selected period and resource</p>
          </div>
          {exportType === 'historical_forecast' && (
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-brand-600 dark:text-brand-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      </button>

      {/* Market Report Option */}
      <button
        type="button"
        onClick={() => setExportType('market')}
        className={`w-full text-left p-3 rounded-lg border transition-all ${
          exportType === 'market'
            ? 'border-green-500 bg-green-50 dark:bg-green-900/30 ring-2 ring-green-500 ring-opacity-50'
            : 'border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 hover:border-green-300 dark:hover:border-green-700'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <FontAwesomeIcon icon={faFileExcel} className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">Market Report</span>
              <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200">Excel</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Forecaster error metrics (RMSE, MAE, etc.) for selected period and resource</p>
          </div>
          {exportType === 'market' && (
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

export const ExportTypeInfo = ({exportType, selectedHorizon}) => {
  return (
    <div className="mb-3 min-h-[52px]">
      {exportType === 'raw_measurement' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
          <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"/>
          </svg>
          <span>Processing may take up to 24 hours. You'll be notified when the export is ready.</span>
        </p>
      )}
      {exportType === 'historical_forecast' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
          <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"/>
          </svg>
          <span>Processing may take up to 24 hours. You'll be notified when the export is ready.</span>
        </p>
      )}
      {exportType === 'market' && (
        <p className="text-xs text-blue-600 dark:text-blue-400 flex items-start gap-1.5 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"/>
          </svg>
          <span>{selectedHorizon === 0
            ? "Intraday reports span many gate closures — processing may take up to 24 hours. You'll be notified when it's ready."
            : "Instant for up to 1 month; longer ranges may take up to 24 hours. You'll be notified when it's ready."}</span>
        </p>
      )}
    </div>
  );
};
